import { Box, CircularProgress, Divider, IconButton, Modal, Paper } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import React from "react";
import Logo from "../Logo";
import { AccessKeyNormalRelation } from "../../api/models/access-key-model";
import { AccessPointData } from "../../api/models/access-points";
import dayjs from "dayjs";
import { AccessGroupsData } from "../../api/models/access-groups";
import { useAppSelector } from "../../app/hooks";
import { selectLabels, selectLanguage } from "../../slices/language";
import IconifyIcon from "../IconifyIcon";
import { selectUserRole } from "../../slices/user";
import AccessPointsMap from "../AccessPointsMap";
import {
  formatAccessScheduleLabel,
  formatAccessScheduleTime,
  formatAllowedDaysLabel,
  getAccessPhotoIcon,
  getAccessPhotoLabel,
  getExitRestrictionFrom,
  getExitRestrictionTo,
  hasExitRestrictionSchedule,
} from "../../utils/accessGroupScheduleDisplay";

type MapAccessPoint = {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
};

const hasValidCoordinates = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): boolean => {
  if (latitude == null || longitude == null) return false;

  const lat = Number(latitude);
  const lng = Number(longitude);

  return !Number.isNaN(lat) && !Number.isNaN(lng);
};

interface AccessKeyDetailsProps {
  isLoading: boolean;
  verifiableUUID: string;
  visitorName: string;
  currentAccessKey: AccessKeyNormalRelation | null;
  accessGroups: AccessGroupsData[];
  isSameDate: boolean;
}

const AccessKeyDetails = ({
  isLoading,
  verifiableUUID,
  visitorName,
  currentAccessKey,
  accessGroups,
  isSameDate,
}: AccessKeyDetailsProps) => {
  const labels = useAppSelector(selectLabels);
  const language = useAppSelector(selectLanguage);
  const userRole = useAppSelector(selectUserRole);
  const isGuard = userRole === "guard";

  const currentLanguage = language.startsWith("es") ? "es" : "en";

  const isAuthorization =
    currentAccessKey?.access_key_type === "authorization";
  const accessKeyType = currentAccessKey?.pass_type;

  const lastTransactionType: {
    type: "try" | "entrance" | "exit";
    message?: { en?: string; es?: string } | null;
  } | null = currentAccessKey?.last_transaction_type || null;

  const message = lastTransactionType?.message?.[currentLanguage] || "";

  const createdAt = currentAccessKey?.createdAt;
  const updatedAt = currentAccessKey?.updatedAt;
  const showEditInfo =
    !!createdAt &&
    !!updatedAt &&
    dayjs(updatedAt).isValid() &&
    dayjs(createdAt).isValid() &&
    dayjs(updatedAt).isAfter(dayjs(createdAt));

  const rawRequirements = currentAccessKey?.requirements;
  const requirementsData = Array.isArray(rawRequirements)
    ? rawRequirements
    : rawRequirements?.data ?? [];
  const exitRestriction = currentAccessKey?.exit_restriction;

  const [mapAccessPoint, setMapAccessPoint] =
    React.useState<MapAccessPoint | null>(null);

  const handleOpenMap = (accPoint: AccessPointData) => {
    if (!hasValidCoordinates(accPoint.latitude, accPoint.longitude)) return;

    setMapAccessPoint({
      id: accPoint.id,
      name: accPoint.name,
      latitude: Number(accPoint.latitude),
      longitude: Number(accPoint.longitude),
    });
  };

  const handleCloseMap = () => {
    setMapAccessPoint(null);
  };

  return (
    <div className="w-100 border-box hidden">
      {isLoading ? (
        <div className="w-100 flex align-items-center justify-content-center mt-12">
          <CircularProgress />
        </div>
      ) : (
        <div className="w-100 border-box flex flex-dir-col gap-4">
          <div
            className="w-100 border-box flex flex-dir-col align-items-center gap-6 p-8"
            style={{
              backgroundColor: "white",
              border: "1px #e0e0e0 solid",
              borderRadius: 6,
            }}
          >
            {isAuthorization && (
              <div>
                <p className="body-text-xl font-w-500">
                  {accessKeyType === "entrance"
                    ? labels.entrance_pass
                    : labels.exit_pass}
                </p>
              </div>
            )}
            <div className="w-100 border-box">
              {currentAccessKey?.daleted_at && (
                <div className="w-100 flex justify-content-center">
                  <p className="body-text-md font-w-500 mb-2 red-color">
                    {labels.deleted_at}
                  </p>
                </div>
              )}
              <div className="w-100 flex flex-dir-col align-items-center justify-content-center my-10">
                <Logo
                  type={"passTrackLogo"}
                  alt="passtrack"
                  className="inv-logo"
                />
                {currentAccessKey?.identifier && (
                  <p
                    className="body-text-xl font-w-500 p-0 m-0 mt-6"
                    style={{ textAlign: "center" }}
                  >
                    {currentAccessKey?.identifier}
                  </p>
                )}
              </div>
              {lastTransactionType && (
                <div className="w-100 flex justify-content-center">
                  <p
                    className={`text-center font-14 font-w-500 ${
                      lastTransactionType?.type === "try"
                        ? "red-color"
                        : "green-color"
                    }`}
                  >
                    {message}
                  </p>
                </div>
              )}
              {!isGuard &&
                currentAccessKey?.access_key_type !==
                  "provider" && (
                  <div
                    className="w-100 flex justify-content-center"
                    style={{ marginTop: 40, marginBottom: 40 }}
                  >
                    <QRCodeCanvas value={`${verifiableUUID}`} size={250} />
                  </div>
                )}
              {visitorName && (
                <p className="body-text-xl font-w-500">{visitorName}</p>
              )}
              <p className="body-text-md mt-2 mb-4">
                {labels.authorized_access_by}:{" "}
                {currentAccessKey?.created?.name}
              </p>
              {showEditInfo && (
                <>
                  <p className="body-text-md mb-2">
                    {labels.last_modification}:
                  </p>
                  <p className="body-text-md mb-4">
                    {`${dayjs(updatedAt)
                      .format("MMMM DD, HH:mm")
                      .charAt(0)
                      .toUpperCase()}${dayjs(updatedAt)
                      .format("MMMM DD, HH:mm")
                      .slice(1)}`}
                  </p>
                </>
              )}
              <Divider />
              <p className="body-text-md mt-4 mb-2">
                {currentAccessKey?.reason_for_visit}
              </p>
              <p className="body-text-md mb-4">
                {isSameDate
                  ? `${dayjs(currentAccessKey?.valid_from)
                      .format("MMMM DD")
                      .charAt(0)
                      .toUpperCase()}${dayjs(
                      currentAccessKey?.valid_from,
                    )
                      .format("MMMM DD")
                      .slice(1)}`
                  : `${dayjs(currentAccessKey?.valid_from)
                      .format("MMMM DD")
                      .charAt(0)
                      .toUpperCase()}${dayjs(
                      currentAccessKey?.valid_from,
                    )
                      .format("MMMM DD")
                      .slice(1)} - ${dayjs(
                      currentAccessKey?.valid_until,
                    )
                      .format("MMMM DD")
                      .charAt(0)
                      .toUpperCase()}${dayjs(
                      currentAccessKey?.valid_until,
                    )
                      .format("MMMM DD")
                      .slice(1)}`}
              </p>
              {currentAccessKey?.valid_from &&
                currentAccessKey?.valid_until && (
                  <p className="body-text-md mb-4">
                    {labels.entry_schedule}:{" "}
                    {dayjs(
                      currentAccessKey?.valid_from,
                    ).format("HH:mm")}{" "}
                    -{" "}
                    {dayjs(
                      currentAccessKey?.valid_until,
                    ).format("HH:mm")}
                  </p>
                )}
              {exitRestriction && (
                <p className="body-text-md mb-4">
                  Salir antes de: {exitRestriction.slice(0, 5)}
                </p>
              )}
              {isAuthorization &&
                currentAccessKey?.access_points?.length >
                  0 && (
                  <p className="body-text-md mb-4">
                    {labels.access_point}:{" "}
                    {currentAccessKey?.access_points?.[0]?.name}
                  </p>
                )}
              {requirementsData.length > 0 && (
                <>
                  <Divider />
                  <div>
                    {requirementsData.map((requirement) => (
                      <div key={requirement?.id} style={{ marginTop: 6 }}>
                        <p className="body-text-md">
                          {requirement?.requirement_type?.name}:{" "}
                          <span style={{ fontWeight: 500 }}>
                            {requirement?.requirement_type?.data_type ===
                            "date"
                              ? dayjs(requirement?.value).format("DD/MM/YYYY")
                              : requirement?.value}
                          </span>
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          {accessGroups &&
            accessGroups.length > 0 &&
            accessGroups.map((item, index) => (
              <div
                key={`${item?.id}-${index}`}
                className="w-100 flex flex-dir-col gap-4"
              >
                {item?.access_points?.map(
                  (accPoint, accPointIndex) => {
                    const configs = (item?.configuration ?? []).filter(
                      (e) => e?.id === accPoint?.id,
                    );
                    const canShowMap = hasValidCoordinates(
                      accPoint?.latitude,
                      accPoint?.longitude,
                    );

                    return (
                      <div
                        key={`${accPointIndex}-${accPoint?.id}`}
                        className="w-100 border-box flex flex-dir-col align-items-center gap-6 p-8"
                        style={{
                          backgroundColor: "white",
                          border: "1px #e0e0e0 solid",
                          borderRadius: 6,
                        }}
                      >
                        <div className="w-100 flex align-items-center">
                          <div
                            className={`flex flex-dir-col align-items-center ${
                              canShowMap ? "pointer" : ""
                            }`}
                            role={canShowMap ? "button" : undefined}
                            tabIndex={canShowMap ? 0 : undefined}
                            onClick={
                              canShowMap
                                ? () => handleOpenMap(accPoint)
                                : undefined
                            }
                            onKeyDown={
                              canShowMap
                                ? (event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      handleOpenMap(accPoint);
                                    }
                                  }
                                : undefined
                            }
                            style={{
                              cursor: canShowMap ? "pointer" : "default",
                              opacity: canShowMap ? 1 : 0.45,
                            }}
                          >
                            <IconifyIcon
                              icon={"mingcute:location-2-fill"}
                              style={{ fontSize: 24 }}
                              className={
                                canShowMap ? "subtitle-color" : "disabled-color"
                              }
                            />
                            <p
                              className={`font-12 font-w-500 uppercase ${
                                canShowMap ? "subtitle-color" : "disabled-color"
                              }`}
                            >
                              {labels.map}
                            </p>
                          </div>
                          <Divider
                            orientation="vertical"
                            flexItem
                            className="mx-8"
                          />
                          <div>
                            <p className="body-text-md font-w-500 mb-2">
                              {accPoint?.name}
                            </p>
                            <p className="body-text-xs">
                              {accPoint?.vanue_name}
                            </p>
                            <Divider className="w-100 my-3" />
                            {configs.map((configItem, configItemIndex) => {
                              const photos = configItem?.photos ?? [];
                              const showExit =
                                hasExitRestrictionSchedule(configItem);

                              return (
                                <div
                                  key={`${configItem?.id}-${configItemIndex}`}
                                  style={{
                                    borderTop:
                                      configItemIndex > 0
                                        ? "1px solid #eee"
                                        : "none",
                                    marginTop: configItemIndex > 0 ? 12 : 0,
                                    paddingTop: configItemIndex > 0 ? 12 : 0,
                                  }}
                                >
                                  {configItem?.description && (
                                    <p className="body-text-xs font-w-500 mb-4">
                                      {configItem.description}
                                    </p>
                                  )}

                                  <div className="w-100 flex flex-dir-col gap-4">
                                    <div>
                                      <p className="body-text-xs font-w-500 mb-1">
                                        {labels.permitted_entry_schedule}
                                      </p>
                                      <p className="body-text-sm">
                                        {formatAccessScheduleLabel(
                                          configItem?.start_time,
                                          configItem?.end_time,
                                          labels.all_day,
                                        )}
                                      </p>
                                    </div>

                                    {showExit && (
                                      <div>
                                        <p className="body-text-xs font-w-500 mb-1">
                                          {labels.permitted_exit_schedule}
                                        </p>
                                        <p className="body-text-sm">
                                          {`${formatAccessScheduleTime(
                                            getExitRestrictionFrom(configItem),
                                          )} - ${formatAccessScheduleTime(
                                            getExitRestrictionTo(configItem),
                                          )}`}
                                        </p>
                                      </div>
                                    )}

                                    <div>
                                      <p className="body-text-xs font-w-500 mb-1">
                                        {labels.days_allowed}
                                      </p>
                                      <p className="body-text-sm">
                                        {formatAllowedDaysLabel(
                                          configItem?.week_days ?? [],
                                          labels,
                                        )}
                                      </p>
                                    </div>

                                    {photos.length > 0 && (
                                      <div>
                                        <p className="body-text-xs font-w-500 mb-2">
                                          {labels.required_photographs}
                                        </p>
                                        <div className="flex flex-wrap align-items-center gap-3">
                                          {photos.map((photo, photoIndex) => (
                                            <div
                                              key={`${photo}-${photoIndex}`}
                                              className="flex align-items-center gap-2"
                                              style={{
                                                border: "1px solid #e6e6e6",
                                                borderRadius: 16,
                                                padding: "4px 10px",
                                              }}
                                            >
                                              <IconifyIcon
                                                icon={getAccessPhotoIcon(photo)}
                                                style={{
                                                  fontSize: 14,
                                                  color: "#10418a",
                                                }}
                                              />
                                              <p className="body-text-xs m-0">
                                                {getAccessPhotoLabel(
                                                  photo,
                                                  labels,
                                                )}
                                              </p>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            ))}
          <Modal open={!!mapAccessPoint} onClose={handleCloseMap}>
            <Box
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                boxSizing: "border-box",
              }}
            >
              <Paper
                style={{
                  width: "100%",
                  maxWidth: 720,
                  boxSizing: "border-box",
                  padding: 16,
                }}
              >
                <div className="w-100 flex align-items-center justify-content-between mb-4">
                  <p className="body-text-md font-w-500">{mapAccessPoint?.name}</p>
                  <IconButton size="small" onClick={handleCloseMap} aria-label="close">
                    <IconifyIcon icon={"material-symbols:close"} />
                  </IconButton>
                </div>
                {mapAccessPoint && (
                  <AccessPointsMap
                    accessPoints={[mapAccessPoint]}
                    focusedAccessPointId={mapAccessPoint.id}
                    height={360}
                    resetButtonVariant="center"
                  />
                )}
              </Paper>
            </Box>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default AccessKeyDetails;
